import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { SafeHtml } from "@/components/ui/safe-html"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Text } from "@/components/ui/text"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { CheckCircle, ChevronLeft, Trophy, XCircle } from "lucide-react"

export const Route = createFileRoute("/(app)/app/teams/$teamId_/quiz/$quizId")({
  component: TeamQuizDetailPage,
})

function TeamQuizDetailPage() {
  const { teamId, quizId } = Route.useParams()

  const breakdown = useQuery(api.quizzes.handlers.getTeamQuizBreakdown, {
    teamId: teamId as Id<"teams">,
    quizId: quizId as Id<"quizzes">,
  })

  if (breakdown === undefined) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    )
  }

  if (!breakdown?.quiz) {
    return (
      <div className="p-4">
        <h1 className="text-3xl font-bold">Quiz not found</h1>
        <p className="text-muted-foreground">
          The quiz you're looking for doesn't exist or results haven't been
          published yet.
        </p>
        <Button className="mt-4" asChild>
          <Link to="/app/teams/$teamId" params={{ teamId }}>
            Back to Team
          </Link>
        </Button>
      </div>
    )
  }

  const { quiz, teamResult, leaderboard } = breakdown
  const currentTeamRank = leaderboard.find((e) => e.isCurrentTeam)?.rank
  const totalTeams = leaderboard.length

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/app/teams/$teamId" params={{ teamId }}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{quiz.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary">{quiz.type}</Badge>
            <Badge className="bg-green-500">Results Published</Badge>
          </div>
        </div>
      </div>

      {/* Team Performance Card */}
      {teamResult ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{teamResult.teamName} Performance</span>
              {currentTeamRank && (
                <Badge
                  variant={currentTeamRank <= 3 ? "default" : "outline"}
                  className={
                    currentTeamRank === 1
                      ? "bg-yellow-500 text-lg px-4 py-1"
                      : currentTeamRank === 2
                        ? "bg-gray-400 text-lg px-4 py-1"
                        : currentTeamRank === 3
                          ? "bg-amber-600 text-lg px-4 py-1"
                          : "text-lg px-4 py-1"
                  }
                >
                  Rank #{currentTeamRank} of {totalTeams}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {teamResult.totalScore}
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Total Score
                </Text>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {teamResult.correctCount}
                </Text>
                <Text className="text-sm text-muted-foreground">Correct</Text>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {teamResult.totalQuestions}
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Total Questions
                </Text>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {Math.round(
                    (teamResult.correctCount / teamResult.totalQuestions) * 100
                  )}
                  %
                </Text>
                <Text className="text-sm text-muted-foreground">Accuracy</Text>
              </div>
            </div>

            {/* Question breakdown */}
            <div>
              <h3 className="font-semibold mb-4">Question Breakdown</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Round</TableHead>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Your Answer</TableHead>
                    <TableHead>Correct Answer</TableHead>
                    <TableHead className="text-right w-20">Points</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamResult.questionScores.map((qs) => (
                    <TableRow key={qs.questionId}>
                      <TableCell>R{qs.roundNumber}</TableCell>
                      <TableCell>{qs.order + 1}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {breakdown.questions.find(
                          (q) => q.questionId === qs.questionId
                        )?.text ? (
                          <SafeHtml
                            html={
                              breakdown.questions.find(
                                (q) => q.questionId === qs.questionId
                              )!.text
                            }
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {qs.isCorrect ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          <span
                            className={
                              qs.isCorrect ? "text-green-600" : "text-red-600"
                            }
                          >
                            {qs.userAnswer}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <SafeHtml html={qs.correctAnswer} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{qs.score}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <Text className="text-lg font-medium">No Results Found</Text>
            <Text className="text-muted-foreground">
              Your team hasn't completed this quiz yet.
            </Text>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
